import type { PillTone } from '@bcecloud/portal-ui';

/**
 * Where a contract stands, from the client's side. The API's status stays
 * "sent" or "viewed" until BOTH parties have signed, so a client who has
 * signed is told apart by `clientSigned`: they are done, and it is BCE's turn.
 */

/** The API's contract status, as the myevents endpoint returns it. */
export const CONTRACT_STATUS = { sent: 'sent', viewed: 'viewed', signed: 'signed' } as const;
export type ContractStatus = (typeof CONTRACT_STATUS)[keyof typeof CONTRACT_STATUS];

export const CLIENT_CONTRACT_STATE = {
  readyToSign: 'ready_to_sign',
  waitingOnBce: 'waiting_on_bce',
  signed: 'signed',
} as const;
export type ClientContractState = (typeof CLIENT_CONTRACT_STATE)[keyof typeof CLIENT_CONTRACT_STATE];

/** The pill for each state: what the client reads beside the contract's name. */
export const CLIENT_CONTRACT_PILL: Readonly<Record<ClientContractState, { readonly label: string; readonly tone: PillTone }>> = {
  [CLIENT_CONTRACT_STATE.readyToSign]: { label: 'Ready to sign', tone: 'info' },
  [CLIENT_CONTRACT_STATE.waitingOnBce]: { label: 'Waiting on Best Corporate Events', tone: 'neutral' },
  [CLIENT_CONTRACT_STATE.signed]: { label: 'Signed', tone: 'success' },
};

/**
 * @param contract - Its API status and whether the client has signed.
 * @returns The state the client should be shown.
 */
export function clientContractState(contract: { readonly status: ContractStatus; readonly clientSigned: boolean }): ClientContractState {
  if (contract.status === CONTRACT_STATUS.signed) return CLIENT_CONTRACT_STATE.signed;
  return contract.clientSigned ? CLIENT_CONTRACT_STATE.waitingOnBce : CLIENT_CONTRACT_STATE.readyToSign;
}
